import { Component, Input, OnInit, OnDestroy, TemplateRef, inject, PLATFORM_ID, HostListener, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { MdbModalRef, MdbModalService } from 'mdb-angular-ui-kit/modal';
import { Subject, timer, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';

export interface OfferStory {
  type: 'template' | 'image';
  template?: TemplateRef<any>;
  imageSrc?: string;
  ctaRoute?: string;
  duration?: number;
}

export interface OfferModalConfig {
  stories: OfferStory[];
  fullScreen?: boolean;
  duration?: number | number[];
  modalClass?: string;
}

@Component({
  selector: 'app-offer-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './offer-modal.component.html',
  styleUrls: ['./offer-modal.component.scss']
})
export class OfferModalComponent implements OnInit, OnDestroy, AfterViewInit {
  private _stories: OfferStory[] = [];
  private router = inject(Router, { optional: true });

  @Input()
  set stories(value: OfferStory[] | null | undefined) {
    this._stories = (value ?? []).filter((story): story is OfferStory => !!story);
    this.refreshStories(true);
  }
  get stories(): OfferStory[] {
    return this._stories;
  }

  @Input() fullScreen: boolean = false;
  @Input() duration: number | number[] = 5000; // durata di default: 5 secondi
  currentTemplateIndex: number = 0;
  progressValues: number[] = [];
  isPaused: boolean = false;
  isMouseDown: boolean = false;
  
  private destroy$ = new Subject<void>();
  private timerSubscription?: Subscription;
  private pauseTime: number = 0; // Timestamp quando si mette in pausa
  private totalPausedTime: number = 0; // Tempo totale in pausa
  private storyStartTime: number = 0; // Quando inizia la storia corrente
  private platformId = inject(PLATFORM_ID);
  private wasPausedBeforeDown: boolean = false; // Stato precedente al mousedown
  private elementRef = inject(ElementRef);
  private hasInitialized = false;
  private touchStartX: number = 0; // Posizione X del touch start per determinare la zona
  private touchZone: 'left' | 'center' | 'right' | null = null; // Zona touch corrente
  private touchHandled: boolean = false; // Flag per prevenire il click dopo un touch

  modalRef?: MdbModalRef<OfferModalComponent>;

  constructor() {}

  ngOnInit(): void {
    this.hasInitialized = true;
    this.refreshStories(false);

    // Avvia la prima storia
    if (isPlatformBrowser(this.platformId) && this.storyCount > 0) {
      this.startProgress();
    }
  }

  ngAfterViewInit(): void {
    // Non serve più - usiamo solo HostListener
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent) {
    const target = event.target as HTMLElement;
    // Controlla se il click è dentro il modal
    if (this.elementRef.nativeElement.contains(target)) {
      // Non mettere in pausa se si clicca su elementi interattivi
      if (target.tagName === 'BUTTON' || target.closest('button') || 
          target.tagName === 'A' || target.closest('a')) {
        return;
      }
      this.pauseProgress();
    }
  }

  @HostListener('document:mouseup', ['$event'])
  onDocumentMouseUp(event: MouseEvent) {
    if (this.isMouseDown) {
      this.resumeProgress();
    }
  }

  @HostListener('document:mouseleave', ['$event'])
  onDocumentMouseLeave(event: MouseEvent) {
    if (this.isMouseDown) {
      this.resumeProgress();
    }
  }

  @HostListener('document:touchstart', ['$event'])
  onDocumentTouchStart(event: TouchEvent) {
    const target = event.target as HTMLElement;
    // Controlla se il touch è dentro il modal
    if (this.elementRef.nativeElement.contains(target)) {
      // Non mettere in pausa se si tocca il pulsante di chiusura o il suo wrapper
      if (target.closest('.btn-close') || target.closest('.close-button-wrapper')) {
        event.stopPropagation();
        return;
      }
      
      // Non mettere in pausa se si tocca un elemento interattivo
      if (target.tagName === 'BUTTON' || target.closest('button') || 
          target.tagName === 'A' || target.closest('a')) {
        // Non prevenire il default per permettere il click sui bottoni
        return;
      }
      
      // Se si tocca una zona touch di navigazione (sinistra o destra), non mettere in pausa
      if (target.closest('.touch-zone-left') || target.closest('.touch-zone-right')) {
        // Le zone touch gestiscono la navigazione, non la pausa
        return;
      }
      
      // Previene il menu contestuale su iOS e mette in pausa nella zona centrale o nel contenuto
      if (target.closest('.touch-zone-center') || !target.closest('.touch-navigation-zones')) {
        event.preventDefault();
        this.pauseProgress();
      }
    }
  }

  @HostListener('document:touchmove', ['$event'])
  onDocumentTouchMove(event: TouchEvent) {
    const target = event.target as HTMLElement;
    // Se si tocca il pulsante di chiusura o il suo wrapper, non prevenire il default
    if (target.closest('.btn-close') || target.closest('.close-button-wrapper')) {
      return;
    }
    
    // Se si tocca un bottone, non prevenire il default
    if (target.tagName === 'BUTTON' || target.closest('button') || 
        target.tagName === 'A' || target.closest('a')) {
      return;
    }
    
    // Se si tocca una zona touch di navigazione, non prevenire il default
    if (target.closest('.touch-zone-left') || target.closest('.touch-zone-right')) {
      return;
    }
    
    // Se il touch è dentro il modal e la story è in pausa, previeni la selezione
    if (this.elementRef.nativeElement.contains(target) && this.isMouseDown) {
      event.preventDefault();
    }
  }

  @HostListener('document:touchend', ['$event'])
  onDocumentTouchEnd(event: TouchEvent) {
    const target = event.target as HTMLElement;
    // Se si tocca il pulsante di chiusura o il suo wrapper, non prevenire il default
    if (target.closest('.btn-close') || target.closest('.close-button-wrapper')) {
      if (this.isMouseDown) {
        this.resumeProgress();
      }
      event.stopPropagation();
      return;
    }
    
    // Se si tocca un bottone, non prevenire il default per permettere il click
    if (target.tagName === 'BUTTON' || target.closest('button') || 
        target.tagName === 'A' || target.closest('a')) {
      // Se era in pausa, riprendi il progresso ma non prevenire il click
      if (this.isMouseDown) {
        this.resumeProgress();
      }
      return;
    }
    
    // Se si tocca una zona touch di navigazione, non mettere in pausa
    if (target.closest('.touch-zone-left') || target.closest('.touch-zone-right')) {
      // Le zone touch gestiscono la navigazione tramite click handler
      // Non fare nulla qui, la navigazione è gestita da onTouchZoneEnd
      if (this.isMouseDown) {
        this.resumeProgress();
      }
      return;
    }
    
    if (this.isMouseDown) {
      // Previeni il menu contestuale quando si rilascia il touch
      event.preventDefault();
      this.resumeProgress();
    }
  }
  
  /**
   * Gestisce il touch start nelle zone di navigazione
   */
  onTouchZoneStart(event: TouchEvent, zone: 'left' | 'right'): void {
    const target = event.target as HTMLElement;
    // Non gestire se si tocca il pulsante di chiusura o il suo wrapper
    if (target.closest('.btn-close') || target.closest('.close-button-wrapper')) {
      event.stopPropagation();
      return;
    }
    
    this.touchZone = zone;
    this.touchHandled = false; // Reset del flag
    if (event.touches.length > 0) {
      this.touchStartX = event.touches[0].clientX;
    }
    // Previeni il default per evitare che si attivi la pausa
    event.stopPropagation();
  }
  
  /**
   * Gestisce il touch end nelle zone di navigazione
   */
  onTouchZoneEnd(event: TouchEvent, zone: 'left' | 'right'): void {
    const target = event.target as HTMLElement;
    // Non gestire se si tocca il pulsante di chiusura o il suo wrapper
    if (target.closest('.btn-close') || target.closest('.close-button-wrapper')) {
      event.stopPropagation();
      return;
    }
    
    event.stopPropagation();
    
    // Verifica che il touch sia nella stessa zona (non c'è stato movimento significativo)
    if (event.changedTouches.length > 0) {
      const touchEndX = event.changedTouches[0].clientX;
      const deltaX = Math.abs(touchEndX - this.touchStartX);
      
      // Se il movimento è minimo (< 15px), considera come click/tap
      if (deltaX < 15 && this.touchZone === zone) {
        this.touchHandled = true; // Marca come gestito per prevenire il click
        // Previeni il click event che verrà emesso dopo il touchend
        event.preventDefault();
        
        if (zone === 'left') {
          this.previousStory();
        } else if (zone === 'right') {
          this.nextStory();
        }
        
        // Reset del flag dopo un breve delay
        setTimeout(() => {
          this.touchHandled = false;
        }, 300);
      }
    }
    this.touchZone = null;
  }
  
  /**
   * Gestisce il click nelle zone touch (solo per desktop/fallback)
   */
  onTouchZoneClick(event: MouseEvent, zone: 'left' | 'right'): void {
    const target = event.target as HTMLElement;
    // Non gestire se si clicca il pulsante di chiusura o il suo wrapper
    if (target.closest('.btn-close') || target.closest('.close-button-wrapper')) {
      event.stopPropagation();
      return;
    }
    
    // Se il touch è già stato gestito (su mobile), previeni il click
    if (this.touchHandled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    
    // Solo su desktop (non touch devices), gestisci il click
    if (zone === 'left') {
      this.previousStory();
    } else if (zone === 'right') {
      this.nextStory();
    }
  }

  /**
   * Gestisce il touch start sul pulsante di chiusura
   */
  onCloseButtonTouchStart(event: TouchEvent): void {
    event.stopPropagation();
    event.stopImmediatePropagation();
    // Previeni che l'evento venga gestito dalle zone touch
    this.touchHandled = true;
    // Previeni il default per evitare comportamenti indesiderati
    event.preventDefault();
  }

  /**
   * Gestisce il touch end sul pulsante di chiusura
   */
  onCloseButtonTouchEnd(event: TouchEvent): void {
    event.stopPropagation();
    event.stopImmediatePropagation();
    event.preventDefault();
    // Chiudi la modale
    this.closeModal();
    // Reset del flag dopo un breve delay
    setTimeout(() => {
      this.touchHandled = false;
    }, 300);
  }

  @HostListener('document:touchcancel', ['$event'])
  onDocumentTouchCancel(event: TouchEvent) {
    if (this.isMouseDown) {
      this.resumeProgress();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopProgress();
  }

  get storyCount(): number {
    return this.stories.length;
  }

  get currentStory(): OfferStory | undefined {
    return this.stories[this.currentTemplateIndex];
  }

  get isImageStory(): boolean {
    return this.currentStory?.type === 'image';
  }

  /**
   * Ottiene la durata per il template corrente
   */
  private getCurrentDuration(): number {
    const story = this.currentStory;
    if (story?.duration) {
      return story.duration;
    }

    if (Array.isArray(this.duration)) {
      return this.duration[this.currentTemplateIndex] || 5000;
    }
    return this.duration;
  }

  /**
   * Avvia il progresso della storia corrente
   */
  private startProgress(): void {
    if (!isPlatformBrowser(this.platformId) || this.storyCount === 0) return;

    this.storyStartTime = Date.now();
    this.totalPausedTime = 0;
    const duration = this.getCurrentDuration();

    const updateInterval = 50; // Aggiorna ogni 50ms per una progressione fluida
    
    this.timerSubscription = timer(0, updateInterval)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.isPaused) {
          const now = Date.now();
          const elapsed = now - this.storyStartTime - this.totalPausedTime;
          const progress = Math.min((elapsed / duration) * 100, 100);
          this.progressValues[this.currentTemplateIndex] = progress;

          if (progress >= 100) {
            this.onProgressComplete();
          }
        }
      });
  }

  /**
   * Ferma il progresso
   */
  private stopProgress(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = undefined;
    }
  }

  /**
   * Chiamato quando una progress bar completa
   */
  private onProgressComplete(): void {
    this.stopProgress();
    
    // Imposta la progress bar corrente a 100
    if (this.progressValues.length > this.currentTemplateIndex) {
      this.progressValues[this.currentTemplateIndex] = 100;
    }
    
    // Passa al template successivo
    if (this.currentTemplateIndex < this.storyCount - 1) {
      this.currentTemplateIndex++;
      this.startProgress();
    } else {
      // Ultima storia completata
      const currentStory = this.currentStory;
      this.closeModal();
      if (currentStory?.ctaRoute && this.router) {
        // Naviga dopo aver chiuso il modal
        setTimeout(() => {
          this.router!.navigate([currentStory.ctaRoute!]);
        }, 100);
      }
    }
  }

  /**
   * Toggle pausa/ripresa al click
   */
  togglePause(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.isPaused = !this.isPaused;
    
    if (this.isPaused) {
      // Salva il momento della pausa
      this.pauseTime = Date.now();
    } else {
      // Calcola quanto tempo è passato in pausa e aggiungilo al totale
      if (this.pauseTime > 0) {
        this.totalPausedTime += Date.now() - this.pauseTime;
        this.pauseTime = 0;
      }
    }
  }

  /**
   * Mette in pausa il progresso (per mousedown/touchstart)
   */
  pauseProgress(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    
    this.isMouseDown = true;
    // Salva lo stato precedente
    this.wasPausedBeforeDown = this.isPaused;
    
    // Mette in pausa solo se non era già in pausa
    if (!this.isPaused) {
      this.isPaused = true;
      this.pauseTime = Date.now();
    }
  }

  /**
   * Riprende il progresso (per mouseup/touchend)
   */
  resumeProgress(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    
    if (!this.isMouseDown) return;
    this.isMouseDown = false;
    
    // Riprende solo se era stato messo in pausa dal mousedown
    if (this.isPaused && !this.wasPausedBeforeDown && this.pauseTime > 0) {
      this.isPaused = false;
      // Calcola quanto tempo è passato in pausa e aggiungilo al totale
      this.totalPausedTime += Date.now() - this.pauseTime;
      this.pauseTime = 0;
    }
  }

  /**
   * Passa alla storia successiva manualmente
   */
  nextStory(): void {
    if (this.currentTemplateIndex < this.storyCount - 1) {
      this.stopProgress();
      if (this.progressValues.length > this.currentTemplateIndex) {
        this.progressValues[this.currentTemplateIndex] = 100;
      }
      this.currentTemplateIndex++;
      this.startProgress();
    } else {
      // Ultima storia, naviga se c'è un ctaRoute
      const currentStory = this.currentStory;
      this.closeModal();
      if (currentStory?.ctaRoute && this.router) {
        // Naviga dopo aver chiuso il modal
        setTimeout(() => {
          this.router!.navigate([currentStory.ctaRoute!]);
        }, 100);
      }
    }
  }

  /**
   * Torna alla storia precedente manualmente
   */
  previousStory(): void {
    if (this.currentTemplateIndex > 0) {
      this.stopProgress();
      if (this.progressValues.length > this.currentTemplateIndex) {
        this.progressValues[this.currentTemplateIndex] = 0;
      }
      this.currentTemplateIndex--;
      if (this.progressValues.length > this.currentTemplateIndex) {
        this.progressValues[this.currentTemplateIndex] = 0;
      }
      this.startProgress();
    }
  }

  /**
   * Gestisce il click sull'immagine della storia
   */
  onStoryImageClick(event: MouseEvent): void {
    const currentStory = this.currentStory;
    // Se la storia ha un ctaRoute, naviga e chiudi il modal
    if (currentStory?.ctaRoute && this.router) {
      event.stopPropagation();
      this.closeModal();
      setTimeout(() => {
        this.router!.navigate([currentStory.ctaRoute!]);
      }, 100);
    }
    // Altrimenti il comportamento di pausa è gestito dagli handler globali
  }

  /**
   * Gestisce il click sulla zona centrale (per CTA)
   */
  onCenterZoneClick(event: MouseEvent): void {
    const currentStory = this.currentStory;
    // Se la storia ha un ctaRoute, naviga e chiudi il modal
    if (currentStory?.ctaRoute && this.router) {
      event.stopPropagation();
      this.closeModal();
      setTimeout(() => {
        this.router!.navigate([currentStory.ctaRoute!]);
      }, 100);
    }
  }

  /**
   * Gestisce il touchstart sulla zona centrale (per CTA mobile) - previene la pausa
   */
  onCenterZoneTouchStart(event: TouchEvent): void {
    const currentStory = this.currentStory;
    // Se la storia ha un ctaRoute, previeni la pausa
    if (currentStory?.ctaRoute && this.router) {
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
  }

  /**
   * Gestisce il touchend sulla zona centrale (per CTA mobile)
   */
  onCenterZoneTouch(event: TouchEvent): void {
    const currentStory = this.currentStory;
    // Se la storia ha un ctaRoute, naviga e chiudi il modal
    if (currentStory?.ctaRoute && this.router) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.closeModal();
      setTimeout(() => {
        this.router!.navigate([currentStory.ctaRoute!]);
      }, 100);
    }
  }

  /**
   * Gestisce il touchstart sull'immagine della storia (mobile) - previene la pausa
   */
  onStoryImageTouchStart(event: TouchEvent): void {
    const currentStory = this.currentStory;
    // Se la storia ha un ctaRoute, previeni la pausa
    if (currentStory?.ctaRoute && this.router) {
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
  }

  /**
   * Gestisce il touchend sull'immagine della storia (mobile)
   */
  onStoryImageTouch(event: TouchEvent): void {
    const currentStory = this.currentStory;
    // Se la storia ha un ctaRoute, naviga e chiudi il modal
    if (currentStory?.ctaRoute && this.router) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.closeModal();
      setTimeout(() => {
        this.router!.navigate([currentStory.ctaRoute!]);
      }, 100);
    }
    // Altrimenti il comportamento di pausa è gestito dagli handler globali
  }

  /**
   * Chiude la modale
   */
  closeModal(): void {
    if (this.modalRef) {
      this.modalRef.close();
    }
  }

  // Rimossi i click handler per disabilitare le interazioni sulle storie

  /**
   * Metodo statico per aprire la modale
   */
  static open(modalService: MdbModalService, config: OfferModalConfig): MdbModalRef<OfferModalComponent> {
    const modalRef = modalService.open(OfferModalComponent, {
      modalClass: config.fullScreen 
        ? `modal-fullscreen ${config.modalClass || ''}` 
        : `modal-dialog-centered ${config.modalClass || ''}`,
      ignoreBackdropClick: false,
      keyboard: true,
      data: {
        stories: config.stories,
        fullScreen: config.fullScreen,
        duration: config.duration
      }
    });

    // Passa i dati al componente
    if (modalRef.component) {
      modalRef.component.stories = config.stories || [];
      modalRef.component.fullScreen = config.fullScreen || false;
      modalRef.component.duration = config.duration || 5000;
      modalRef.component.modalRef = modalRef;
    }

    return modalRef;
  }

  resolveImagePath(imagePath: string): string {
    if (!imagePath) {
      return '';
    }

    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }

    if (imagePath.startsWith('/')) {
      return imagePath;
    }

    if (imagePath.startsWith('stories/')) {
      return `/${imagePath}`;
    }

    return `/stories/${imagePath}`;
  }

  private refreshStories(resetProgress: boolean): void {
    if (!this.hasInitialized) {
      return;
    }

    const storyCount = this.storyCount;

    this.currentTemplateIndex = resetProgress
      ? 0
      : storyCount > 0
        ? Math.min(this.currentTemplateIndex, storyCount - 1)
        : 0;
    this.progressValues = new Array(storyCount).fill(0);

    if (resetProgress) {
      this.stopProgress();
      if (isPlatformBrowser(this.platformId) && storyCount > 0) {
        this.startProgress();
      }
    }
  }
}

